import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  QrCode,
  Printer,
  Download,
  RefreshCw,
  Save,
  AlertCircle,
} from "lucide-react";
import { Product, StickerConfiguration } from "@/types/stock";
import { useStock } from "@/context/StockContext";
import { generateProductEAN13Barcode } from "@/utils/barcodeUtils";
import BarcodeSticker from "./BarcodeSticker";
import StickerConfigDialog from "./StickerConfigDialog";
import { toast } from "sonner";
import { defaultStickerConfig } from "@/config/appConfig";
import { useLocalStorage } from "@/hooks/useLocalStorage";

interface BarcodeStickerDialogProps {
  product: Product;
  trigger?: React.ReactNode;
  onBarcodeGenerated?: (barcode: string) => void;
}

const BarcodeStickerDialog: React.FC<BarcodeStickerDialogProps> = ({
  product,
  trigger,
  onBarcodeGenerated,
}) => {
  const { t } = useTranslation();
  const { updateProduct } = useStock();
  const [open, setOpen] = useState(false);
  const [generatedBarcode, setGeneratedBarcode] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [stickerCount, setStickerCount] = useState(1);
  const stickerRef = useRef<HTMLDivElement>(null);

  // Store sticker configuration in localStorage
  const [stickerConfig, setStickerConfig] =
    useLocalStorage<StickerConfiguration>(
      "stickerConfiguration",
      defaultStickerConfig
    );

  const handleConfigChange = (newConfig: StickerConfiguration) => {
    setStickerConfig(newConfig);
  };

  const handleConfigReset = () => {
    setStickerConfig(defaultStickerConfig);
  };

  // Print function using browser's native print functionality
  const handlePrint = () => {
    if (!stickerRef.current) {
      toast.error("Sticker content not ready for printing");
      return;
    }

    // Show loading toast
    toast.info(t("stock.preparingPrint") || "Preparing print...");

    // Create a new window with just the sticker content
    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) {
      toast.error("Please allow popups to enable printing");
      return;
    }

    // Get the complete outer HTML including the sticker container
    const stickerHTML = stickerRef.current.outerHTML;

    // Convert mm to inches for @page size
    const widthInInches = (stickerConfig.dimensions.width / 25.4).toFixed(2);
    const heightInInches = (stickerConfig.dimensions.height / 25.4).toFixed(2);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Barcode Sticker - ${product.name}</title>
          <meta charset="utf-8">
          <style>
            * {
              box-sizing: border-box;
            }

            @page {
              size: ${widthInInches}in ${heightInInches}in;
              margin: 0;
            }

            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100%;
            }

            body {
              font-family: Arial, sans-serif;
              background: white;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: flex-start;
              padding: 20px;
            }

            /* Utility classes for layout */
            .text-center { text-align: center; }
            .text-black { color: #000000; }
            .text-gray-600 { color: #4b5563; }
            .text-gray-500 { color: #6b7280; }
            .text-green-600 { color: #16a34a; }
            .font-bold { font-weight: bold; }
            .leading-tight { line-height: 1.25; }
            .mb-1 { margin-bottom: 0.25rem; }
            .flex { display: flex; }
            .justify-center { justify-content: center; }
            .items-center { align-items: center; }
            .max-w-full { max-width: 100%; }
            .h-auto { height: auto; }
            .bg-white { background-color: white; }
            .border { border-width: 1px; }
            .border-gray-300 { border-color: #d1d5db; }
            .rounded-lg { border-radius: 0.5rem; }
            .p-1 { padding: 0.25rem; }

            /* Ensure proper box-sizing */
            div, span, h3, img {
              box-sizing: border-box;
            }

            @media print {
              html, body {
                margin: 0;
                padding: 0;
                overflow: hidden;
              }

              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                color-adjust: exact;
                padding: 0;
                display: block;
              }

              .no-print {
                display: none !important;
              }

              .print-container {
                width: ${widthInInches}in !important;
                height: ${heightInInches}in !important;
                margin: 0 !important;
                padding: 0 !important;
                border: none !important;
                page-break-after: always;
                overflow: visible !important;
              }

              /* Remove borders and rounded corners in print */
              .border { border: none !important; }
              .rounded-lg { border-radius: 0 !important; }

              /* Ensure sticker takes full page */
              .print-container > div {
                width: ${widthInInches}in !important;
                height: ${heightInInches}in !important;
                margin: 0 !important;
                border: none !important;
                border-radius: 0 !important;
                box-sizing: border-box !important;
                overflow: hidden !important;
                display: flex !important;
                flex-direction: column !important;
                justify-content: center !important;
              }
            }

            .print-container {
              width: ${widthInInches}in;
              height: ${heightInInches}in;
              border: 1px solid #ccc;
              background: white;
              margin: 0 auto;
              overflow: visible;
            }

            .print-button {
              margin: 20px auto;
              display: block;
              padding: 10px 20px;
              background: #007bff;
              color: white;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
            }

            .print-button:hover {
              background: #0056b3;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${stickerHTML}
          </div>
          <script>
            // Wait for content to load, then auto-trigger print dialog
            window.onload = function() {
              // Small delay to ensure images are loaded
              setTimeout(function() {
                window.print();
              }, 250);
            };

            // Close window after printing or canceling
            window.onafterprint = function() {
              setTimeout(function() {
                window.close();
              }, 100);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const generateNewBarcode = async () => {
    try {
      setIsGenerating(true);
      const result = await generateProductEAN13Barcode(product.id);
      setGeneratedBarcode(result.barcode);
      toast.success(t("stock.barcodeGenerated"));
    } catch (error) {
      console.error("Error generating barcode:", error);
      toast.error(t("stock.failedToGenerateBarcode"));
    } finally {
      setIsGenerating(false);
    }
  };

  const saveBarcode = async () => {
    if (!generatedBarcode) {
      toast.error(t("stock.noBarcodeToSave"));
      return;
    }

    try {
      setIsSaving(true);
      await updateProduct(product.id, {
        name: product.name,
        description: product.description,
        sku: product.sku,
        barcode: generatedBarcode,
        categoryId: product.categoryId,
        price: product.price,
        cost: product.cost,
        stockQuantity: product.stockQuantity,
        minStockLevel: product.minStockLevel,
        maxStockLevel: product.maxStockLevel,
        isActive: product.isActive,
        imageUrl: product.imageUrl,
      });

      onBarcodeGenerated?.(generatedBarcode);
      toast.success(t("stock.barcodeSaved"));
      setOpen(false);
    } catch (error) {
      console.error("Error saving barcode:", error);
      toast.error(t("stock.failedToSaveBarcode"));
    } finally {
      setIsSaving(false);
    }
  };

  const downloadSticker = () => {
    if (!stickerRef.current) return;

    // Create a canvas to render the sticker
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size (2.5in x 1.5in at 300 DPI)
    canvas.width = 750; // 2.5 * 300
    canvas.height = 450; // 1.5 * 300

    // Convert the sticker div to canvas (simplified approach)
    // In a real implementation, you might want to use html2canvas library
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add text
    ctx.fillStyle = "black";
    ctx.font = "24px Arial";
    ctx.textAlign = "center";
    ctx.fillText(product.name, canvas.width / 2, 50);

    // Add price
    ctx.font = "18px Arial";
    ctx.fillText(
      `${product.price.toFixed(3)} TND`,
      canvas.width / 2,
      canvas.height - 30
    );

    // Download the canvas as image
    const link = document.createElement("a");
    link.download = `barcode-sticker-${product.sku}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="flex items-center gap-2">
      <QrCode className="h-4 w-4" />
      {t("stock.printSticker")}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            {t("stock.barcodeSticker")} - {product.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Product Info */}
          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{product.name}</h3>
                  <p className="text-sm text-gray-600">{product.description}</p>
                  <p className="text-sm font-mono">SKU: {product.sku}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">
                    {product.price.toFixed(3)} TND
                  </p>
                  {product.barcode ? (
                    <Badge variant="secondary">
                      {t("stock.productHasBarcode")}
                    </Badge>
                  ) : (
                    <Badge variant="outline">
                      {t("stock.productNoBarcode")}
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Barcode Generation for products without barcodes */}
          {!product.barcode && !generatedBarcode && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span className="text-sm">
                    {t("stock.generateNewBarcode")}
                  </span>
                </div>
                <Button
                  onClick={generateNewBarcode}
                  disabled={isGenerating}
                  className="w-full"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <QrCode className="h-4 w-4 mr-2" />
                      {t("stock.generateEAN13Barcode")}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Sticker Preview - Show if product has barcode or barcode was generated */}
          {(product.barcode || generatedBarcode) && (
            <>
              <Card>
                <CardContent className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold">
                      {t("stock.stickerPreview")}
                    </h3>
                    <div className="flex items-center gap-2">
                      <StickerConfigDialog
                        config={stickerConfig}
                        onConfigChange={handleConfigChange}
                        onReset={handleConfigReset}
                      />
                      <Label htmlFor="stickerCount" className="text-sm">
                        {t("stock.copies")}:
                      </Label>
                      <Input
                        id="stickerCount"
                        type="number"
                        min="1"
                        max="50"
                        value={stickerCount}
                        onChange={(e) =>
                          setStickerCount(parseInt(e.target.value) || 1)
                        }
                        className="w-20"
                      />
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <BarcodeSticker
                      ref={stickerRef}
                      product={product}
                      barcode={generatedBarcode || product.barcode}
                      config={stickerConfig}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handlePrint}
                  className="flex items-center gap-2"
                >
                  <Printer className="h-4 w-4" />
                  {t("stock.printSticker")}
                </Button>

                <Button
                  onClick={downloadSticker}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  {t("stock.downloadSticker")}
                </Button>

                {/* Save barcode button - only show if barcode was generated and not saved yet */}
                {generatedBarcode && !product.barcode && (
                  <Button
                    onClick={saveBarcode}
                    disabled={isSaving}
                    variant="secondary"
                    className="flex items-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        {t("stock.saveBarcodeToProduct")}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeStickerDialog;
